import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, GitMerge, Tag as TagIcon, Undo2 } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { EmptyState } from '@/components/ui/states';
import { rowsOf, useApi } from '@/Hooks/useApi';
import {
  ERASURE_CONFIRMATION,
  apiError,
  mergeCounts,
  type Customer360,
  type CustomerMergeRecord,
  type CustomerTag,
} from '../lib';
import MergeDialog from './MergeDialog';

/**
 * Identity, tags, merge history and the two irreversible privacy actions.
 *
 * Everything that touches another customer's row (merge, reverse) or another
 * person's rights over their own data (export, erase) lives here rather than
 * on the overview tab, gated per-action as well as by the tab existing at all
 * — a manager who cannot merge must not see a button that 403s.
 */
export default function IdentityTab({
  customerId,
  profile,
  onChanged,
}: {
  customerId: string;
  profile: Customer360;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm, prompt: eraseConfirm } = useConfirm();
  const canMerge = can('customers.merge');
  const canManageTags = can('customers.update');
  const canExport = can('privacy.export');
  const canErase = can('privacy.erase');

  const [mergeOpen, setMergeOpen] = useState(false);
  const [merges, setMerges] = useState<CustomerMergeRecord[]>(profile.merges);
  const [reversing, setReversing] = useState<string | null>(null);
  const [erasing, setErasing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => setMerges(profile.merges), [profile.merges]);

  const allTags = useApi<unknown>(canManageTags ? '/customer-tags' : null, { limit: 200 });
  const tagOptions = rowsOf<CustomerTag>(allTags.data).rows;

  const toggleTag = async (tagId: string, assigned: boolean) => {
    try {
      if (assigned) await api.delete(`/customers/${customerId}/tags/${tagId}`);
      else await api.post(`/customers/${customerId}/tags/${tagId}`);
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.tags.updateFailed')));
    }
  };

  const reverseMerge = async (merge: CustomerMergeRecord) => {
    const ok = await confirm({
      title: t('crm.merge.reverseConfirmTitle'),
      body: t('crm.merge.reverseConfirmBody'),
      confirmLabel: t('crm.merge.reverse'),
      tone: 'danger',
    });
    if (!ok) return;
    setReversing(merge.id);
    try {
      await api.post(`/customers/merges/${merge.id}/reverse`, {});
      toast.success(t('crm.merge.reversed'));
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.merge.reverseFailed')));
    } finally {
      setReversing(null);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/export`);
      // The viewer's sandbox and most browser contexts here have no reliable
      // download surface, so the export opens as a JSON blob URL in a new tab
      // for the operator to save themselves, rather than pretending a
      // programmatic file save exists.
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(apiError(e, t('crm.privacy.exportFailed')));
    } finally {
      setExporting(false);
    }
  };

  const doErase = async () => {
    const typed = await eraseConfirm({
      title: t('crm.privacy.eraseConfirmTitle'),
      body: t('crm.privacy.eraseConfirmBody'),
      label: t('crm.privacy.eraseConfirmLabel', { word: ERASURE_CONFIRMATION }),
      confirmLabel: t('crm.privacy.erase'),
      tone: 'danger',
      validate: (value) =>
        value === ERASURE_CONFIRMATION ? null : t('crm.privacy.eraseConfirmMismatch'),
    });
    if (typed === null) return;
    setErasing(true);
    try {
      await api.post(`/customers/${customerId}/erase`, { confirm: ERASURE_CONFIRMATION });
      toast.success(t('crm.privacy.erased'));
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('crm.privacy.eraseFailed')));
    } finally {
      setErasing(false);
    }
  };

  const assignedIds = new Set(profile.tags.map((tag) => tag.id));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('crm.identity.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-muted">{t('crm.identity.primaryPhone')}</p>
            <p dir="ltr" className="text-start">{profile.identity.primaryPhone ?? '—'}</p>
          </div>
          {profile.identity.mergedPhones.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-muted">{t('crm.identity.mergedPhones')}</p>
              <p className="text-xs text-ink-muted">{t('crm.identity.mergedPhonesHint')}</p>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {profile.identity.mergedPhones.map((phone) => (
                  <li key={phone} dir="ltr" className="rounded-full bg-subtle px-2.5 py-1 text-xs">
                    {phone}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {profile.customer.erasedAt && (
            <Badge tone="danger">{t('crm.privacy.erasedBadge')}</Badge>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Icon as={TagIcon} size="sm" />
            {t('crm.tags.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!canManageTags ? (
            profile.tags.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('crm.tags.none')}</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {profile.tags.map((tag) => (
                  <Badge key={tag.id} tone="neutral">
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {tagOptions.length === 0 && !allTags.loading && (
                <p className="text-sm text-ink-muted">{t('crm.tags.noneDefined')}</p>
              )}
              {tagOptions.map((tag) => {
                const assigned = assignedIds.has(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => void toggleTag(tag.id, assigned)}
                    className={
                      'rounded-full border px-2.5 py-1 text-xs ' +
                      (assigned
                        ? 'border-accent bg-accent-soft text-accent-soft-fg'
                        : 'border-line-strong text-ink-muted hover:bg-hover')
                    }
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Icon as={GitMerge} size="sm" />
            {t('crm.merge.historyTitle')}
          </CardTitle>
          {canMerge && !profile.customer.erasedAt && (
            <Button size="sm" variant="outline" onClick={() => setMergeOpen(true)}>
              {t('crm.merge.mergeDuplicate')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {merges.length === 0 ? (
            <EmptyState title={t('crm.merge.noHistory')} hint={t('crm.merge.noHistoryHint')} />
          ) : (
            <ul className="divide-y text-sm">
              {merges.map((merge) => {
                const counts = mergeCounts(merge);
                const isWinnerHere = merge.winnerId === customerId;
                return (
                  <li key={merge.id} className="space-y-1 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        {isWinnerHere
                          ? t('crm.merge.absorbedInto', {
                              name: merge.loser?.name ?? merge.loserId,
                            })
                          : t('crm.merge.mergedInto', {
                              name: merge.winner?.name ?? merge.winnerId,
                            })}
                      </span>
                      <Badge tone={merge.status === 'REVERSED' ? 'neutral' : 'success'}>
                        {t(`crm.merge.status.${merge.status}`)}
                      </Badge>
                    </div>
                    <p className="text-xs text-ink-muted">
                      {t('crm.merge.summaryLine', {
                        date: formatDateTime(merge.createdAt, i18n.language),
                        points: merge.pointsMoved,
                      })}
                    </p>
                    {canMerge && merge.status === 'COMPLETED' && isWinnerHere && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!counts.reversible || Boolean(merge.snapshotRedactedAt) || reversing === merge.id}
                        onClick={() => void reverseMerge(merge)}
                        title={
                          merge.snapshotRedactedAt
                            ? t('crm.merge.snapshotRedacted')
                            : !counts.reversible
                              ? t('crm.merge.notReversible')
                              : undefined
                        }
                      >
                        <Icon as={Undo2} size="sm" className="me-1.5" />
                        {t('crm.merge.reverse')}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {(canExport || canErase) && (
        <Card>
          <CardHeader>
            <CardTitle>{t('crm.privacy.title')}</CardTitle>
            <p className="text-xs text-ink-muted">{t('crm.privacy.subtitle')}</p>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {canExport && (
              <Button variant="outline" size="sm" onClick={() => void doExport()} disabled={exporting}>
                <Icon as={Download} size="sm" className="me-1.5" />
                {t('crm.privacy.export')}
              </Button>
            )}
            {canErase && !profile.customer.erasedAt && (
              <Button variant="danger" size="sm" onClick={() => void doErase()} disabled={erasing}>
                {t('crm.privacy.erase')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {mergeOpen && (
        <MergeDialog
          winnerId={customerId}
          winnerName={profile.customer.name}
          onClose={() => setMergeOpen(false)}
          onMerged={() => {
            setMergeOpen(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
